import { TableMenu } from '@/components'
import { cn } from '@/lib/utils'
import { getWeekdayName } from '@/utils/time'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format } from 'date-fns'
import type { PeriodAddonForm } from './PeriodAddonPrompt'

const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

interface PeriodAddonViewProps {
  periodAddon: Pick<
    PeriodAddonForm,
    | 'id'
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday'
    | 'start'
    | 'end'
    | 'salaryCodeName'
  >
  onEdit: () => void
  onDelete: () => void
}

export const PeriodAddonView: React.FC<PeriodAddonViewProps> = ({
  periodAddon,
  onEdit,
  onDelete,
}) => {
  const translations = useTranslate({
    edit: 'common.edit',
    delete: 'common.delete-alt',
    wholeDay: 'common.whole-day',
  })

  const intervalDisplayString =
    format(periodAddon.start, 'HH:mm') +
    ' - ' +
    format(periodAddon.end, 'HH:mm')
  const wholeDay =
    periodAddon.start.getHours() === 0 &&
    periodAddon.end.getMinutes() === 0 &&
    periodAddon.end.getHours() === 0 &&
    periodAddon.end.getMinutes() === 0

  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-stone-200 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex gap-1">
          {WEEKDAYS.map((day, index) => (
            <span
              key={day}
              className={cn(
                'rounded-md px-1.5 py-0.5 text-xs font-semibold',
                periodAddon[day]
                  ? 'bg-secondary text-neutral-50'
                  : 'bg-stone-100 text-stone-400'
              )}
            >
              {getWeekdayName(index, 'sm')}
            </span>
          ))}
        </div>
        <span className="text-sm font-medium text-stone-900">
          {wholeDay ? translations.wholeDay : intervalDisplayString}
        </span>
        {periodAddon.salaryCodeName && (
          <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-0.5 text-xs font-semibold text-stone-700">
            {periodAddon.salaryCodeName}
          </span>
        )}
      </div>
      <TableMenu
        items={[
          {
            id: 'edit',
            type: 'regular',
            label: translations.edit,
            icon: 'pen',
            onClick: onEdit,
          },
          {
            id: 'delete',
            type: 'regular',
            label: translations.delete,
            icon: 'trash',
            onClick: onDelete,
          },
        ]}
      />
    </div>
  )
}
